import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { Container, Eyebrow, Display, Lead, useReveal } from "./ui";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { stats, impactLead } from "../../pages/landingContent";

/* ---------- Impact stats ---------- */

const Band = styled.section`
  position: relative;
  background: ${colors.forest};
  color: ${colors.linen};
  padding: 120px 0;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    width: 900px;
    height: 900px;
    right: -300px;
    top: -380px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(184, 115, 51, 0.28), transparent);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    width: 700px;
    height: 700px;
    left: -260px;
    bottom: -360px;
    border-radius: 50%;
    background: radial-gradient(closest-side, rgba(157, 184, 191, 0.16), transparent);
    pointer-events: none;
  }

  @media ${media.large} {
    padding: 160px 0;
  }
`;

const BandHead = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 72px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-top: 1px solid rgba(250, 247, 241, 0.16);

  @media ${media.small} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  padding: 32px 28px 36px 0;
  border-bottom: 1px solid rgba(250, 247, 241, 0.16);

  @media ${media.large} {
    border-bottom: 0;
    padding: 40px 28px 0 0;
    & + & {
      padding-left: 28px;
      border-left: 1px solid rgba(250, 247, 241, 0.16);
    }
  }
`;

const StatPrefix = styled.span`
  display: block;
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.sage};
  min-height: 18px;
`;

const StatValue = styled.div`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(72px, 8vw, 116px);
  line-height: 1;
  letter-spacing: -0.05em;
  margin: 10px 0 18px;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg, ${colors.linen} 30%, ${colors.copperLight} 130%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatLabel = styled.p`
  font-family: ${fonts.body};
  font-size: 17px;
  line-height: 1.5;
  color: rgba(250, 247, 241, 0.75);
  max-width: 260px;
`;

const Spark = styled.svg`
  display: block;
  width: 100%;
  max-width: 220px;
  height: 28px;
  margin-top: 22px;

  path {
    stroke-dasharray: 400;
    stroke-dashoffset: 0;
  }
`;

const sparkPaths = [
  "M0 24 C40 24 60 20 90 16 S150 6 220 3",
  "M0 22 C30 18 50 20 80 14 S150 10 220 6",
  "M0 25 L40 22 L70 23 L110 14 L150 12 L190 5 L220 3",
  "M0 26 C50 25 80 22 120 18 C160 14 190 8 220 2",
];

export const Impact = () => {
  const scope = useRef(null);
  useReveal(scope);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray("[data-count]").forEach((el) => {
        const target = Number(el.dataset.count);
        const suffix = el.dataset.suffix || "";
        if (prefersReducedMotion()) {
          el.textContent = `${target}${suffix}`;
          return;
        }
        const counter = { v: 0 };
        el.textContent = `0${suffix}`;
        gsap.to(counter, {
          v: target,
          duration: 2.2,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
          onUpdate: () => {
            el.textContent = `${Math.round(counter.v)}${suffix}`;
          },
        });
      });
      if (!prefersReducedMotion()) {
        gsap.from("[data-spark] path", {
          strokeDashoffset: 400,
          duration: 2,
          ease: "power2.out",
          stagger: 0.15,
          scrollTrigger: { trigger: scope.current, start: "top 60%", once: true },
        });
      }
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <Band id="impact" ref={scope} aria-labelledby="impact-title">
      <Container>
        <BandHead>
          <div data-reveal>
            <Eyebrow $color={colors.sage} $onDark>
              Measured impact
            </Eyebrow>
            <Display id="impact-title" $onDark style={{ marginTop: 20 }}>
              Faster minds, <em>longer memories.</em>
            </Display>
          </div>
          <Lead $onDark data-reveal="0.1">
            {impactLead}
          </Lead>
        </BandHead>
        <StatGrid>
          {stats.map((s, i) => (
            <Stat key={s.label} data-reveal={i * 0.08}>
              <StatPrefix>{s.prefix}</StatPrefix>
              <StatValue data-count={s.value} data-suffix={s.suffix}>
                {s.value}
                {s.suffix}
              </StatValue>
              <StatLabel>{s.label}</StatLabel>
              <Spark data-spark viewBox="0 0 220 28" fill="none" aria-hidden="true">
                <path
                  d={sparkPaths[i]}
                  stroke={[colors.copperLight, colors.sage, colors.mist, colors.clay][i]}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </Spark>
            </Stat>
          ))}
        </StatGrid>
      </Container>
    </Band>
  );
};
